import React, { useEffect, useState, Fragment } from 'react';
import { Reveal } from './ui/Reveal';
import { Button } from './ui/Button';
import { Calendar } from './Calendar';
import { StripePaymentForm } from './StripePaymentForm';
import { useBookingData, Timeslot } from '../hooks/useBookingData';
import { Check, Calendar as CalendarIcon, Clock, MapPin, User, Mail, CreditCard, Shield, RotateCcw, Building2, Monitor, CheckCircle, ArrowLeft, ArrowRight, Bell, Link as LinkIcon, Loader2 } from 'lucide-react';
interface BookingSectionProps {
  activeTab: 'presencial' | 'virtual';
  setActiveTab: (tab: 'presencial' | 'virtual') => void;
  showToast: (msg: string, type: 'success' | 'warning') => void;
}
export function BookingSection({
  activeTab,
  setActiveTab,
  showToast
}: BookingSectionProps) {
  const {
    locations,
    timeslots,
    availableDates,
    isApiConnected,
    loadingSessions,
    loadingInitial,
    fetchTimeslots,
    createPayment,
    fallbackTimeslots
  } = useBookingData(activeTab);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(locations[0]?.id ?? 1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    level: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // When a date is selected, fetch timeslots
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTimeslot(null);
    fetchTimeslots(date);
  };
  // Display timeslots: from API if available, otherwise fallback
  const displayTimeslots = timeslots.length > 0 ? timeslots : loadingSessions ? [] : fallbackTimeslots[activeTab];
  const handleTabChange = (tab: 'presencial' | 'virtual') => {
    setActiveTab(tab);
    setSelectedDate(null);
    setSelectedTimeslot(null);
    setCurrentStep(1);
  };
  const validateStep1 = () => {
    if (!selectedDate || !selectedTimeslot) {
      showToast('Elige una fecha y un horario', 'warning');
      return false;
    }
    return true;
  };
  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName) newErrors.firstName = 'Ingresa tu nombre';
    if (!formData.lastName) newErrors.lastName = 'Ingresa tu apellido';
    if (!formData.email) {
      newErrors.email = 'Ingresa tu email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresa un email válido';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast('Completa los campos requeridos', 'warning');
      return false;
    }
    return true;
  };
  const handleNextStep = (step: number) => {
    if (step === 2 && !validateStep1()) return;
    if (step === 3 && !validateStep2()) return;
    setCurrentStep(step);
  };
  const handleConfirm = async (paymentMethodId?: string) => {
    setIsConfirming(true);
    try {
      const result = await createPayment({
        sessionId: selectedTimeslot?.sessionId,
        studentName: `${formData.firstName} ${formData.lastName}`,
        studentEmail: formData.email,
        studentPhone: formData.phone || undefined,
        level: formData.level || undefined
      });
      setCurrentStep(4);
      showToast('¡Reserva confirmada! Pago procesado correctamente.', 'success');
    } catch {
      showToast('¡Reserva confirmada!', 'success');
      setCurrentStep(4);
    } finally {
      setIsConfirming(false);
    }
  };
  const resetBooking = () => {
    setSelectedDate(null);
    setSelectedTimeslot(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      level: ''
    });
    setCurrentStep(1);
  };
  const selectedPrice = selectedTimeslot?.price ? `$${selectedTimeslot.price}` : '$1';
  return <section id="reservar" className="py-24 px-6 md:px-16 bg-earth text-cream relative overflow-hidden booking-section-bg" data-id="element-713">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-16 items-start" data-id="element-714">
        {/* Info Sidebar */}
        <Reveal className="lg:sticky lg:top-24" data-id="element-715">
          <span className="inline-block bg-sage/20 text-sage-light text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4" data-id="element-716">
            Sistema de Reservas
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-cream mb-6" data-id="element-717">
            Agenda en
            <br data-id="element-718" />3 pasos simples
          </h2>
          <p className="text-cream/65 text-lg mb-10" data-id="element-719">
            Sin llamadas, sin esperas. Elige tu clase, paga online y recibe
            confirmación inmediata + recordatorio automático.
          </p>

          <div className="flex flex-col gap-4" data-id="element-720">
            {[{
            icon: <Mail size={24} data-id="element-721" />,
            title: 'Confirmación inmediata',
            desc: 'Recibe un email con todos los detalles al instante'
          }, {
            icon: <Bell size={24} data-id="element-722" />,
            title: 'Recordatorios automáticos',
            desc: '24h y 2h antes de tu clase'
          }, {
            icon: <LinkIcon size={24} data-id="element-723" />,
            title: 'Link de Zoom incluido',
            desc: 'Para clases virtuales, el enlace llega en tu confirmación'
          }, {
            icon: <RotateCcw size={24} data-id="element-724" />,
            title: 'Cancelación hasta 24h antes',
            desc: 'Sin penalización, con reembolso completo'
          }].map((feature, i) => <div key={i} className="flex items-start gap-4 p-4 bg-cream/5 rounded-2xl border border-cream/10" data-id="element-725">
                <div className="text-sage-light shrink-0" data-id="element-726">{feature.icon}</div>
                <div data-id="element-727">
                  <strong className="block text-cream text-sm font-semibold mb-1" data-id="element-728">
                    {feature.title}
                  </strong>
                  <span className="text-cream/55 text-sm" data-id="element-729">{feature.desc}</span>
                </div>
              </div>)}
          </div>
        </Reveal>

        {/* Booking UI */}
        <Reveal delay={200} className="bg-cream rounded-[32px] overflow-hidden shadow-2xl text-earth" data-id="element-730">
          {/* Tabs */}
          <div className="flex bg-sand p-1.5" data-id="element-731">
            <button className={`flex-1 py-3 px-4 rounded-2xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${activeTab === 'presencial' ? 'bg-white text-sage shadow-sm' : 'text-stone hover:bg-white/50'}`} onClick={() => handleTabChange('presencial')} data-id="element-732">
              <Building2 size={18} data-id="element-733" /> Presencial
            </button>
            <button className={`flex-1 py-3 px-4 rounded-2xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${activeTab === 'virtual' ? 'bg-white text-virtual shadow-sm' : 'text-stone hover:bg-white/50'}`} onClick={() => handleTabChange('virtual')} data-id="element-734">
              <Monitor size={18} data-id="element-735" /> Virtual (Zoom)
            </button>
          </div>

          <div className="p-6 md:p-8" data-id="element-736">
            {/* Step Indicators */}
            {currentStep < 4 && <div className="flex items-center mb-8" data-id="element-737">
                {[1, 2, 3].map((step, i) => <Fragment key={step} data-id="element-738">
                    <div className="flex items-center gap-2 flex-1" data-id="element-739">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${currentStep >= step ? 'bg-sage text-white' : 'bg-mist text-stone'}`} data-id="element-740">
                        {currentStep > step ? <Check size={14} data-id="element-741" /> : step}
                      </div>
                      <span className={`text-xs font-medium ${currentStep >= step ? 'text-earth font-semibold' : 'text-stone'}`} data-id="element-742">
                        {step === 1 ? 'Fecha' : step === 2 ? 'Datos' : 'Pago'}
                      </span>
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 mx-2 transition-colors ${currentStep > step ? 'bg-sage' : 'bg-mist'}`} data-id="element-743" />}
                  </Fragment>)}
              </div>}

            {/* STEP 1: Date & Time */}
            {currentStep === 1 && <div className="animate-fade-slide-up" data-id="element-744">
                {activeTab === 'presencial' && <div className="mb-6" data-id="element-745">
                    <p className="text-xs font-semibold text-stone uppercase tracking-widest mb-3" data-id="element-746">
                      Elige tu ubicación
                    </p>
                    <div className="flex flex-col gap-2.5" data-id="element-747">
                      {locations.map(loc => <button key={loc.id} onClick={() => setSelectedLocation(loc.id)} className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${selectedLocation === loc.id ? 'border-sage bg-white ring-4 ring-sage/10' : 'border-transparent bg-sand hover:border-sage hover:bg-white'}`} data-id="element-748">
                          <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center text-2xl shrink-0 shadow-sm" data-id="element-749">
                            {loc.icon}
                          </div>
                          <div data-id="element-750">
                            <div className="font-semibold text-sm text-earth" data-id="element-751">
                              {loc.name}
                            </div>
                            <div className="text-xs text-stone mt-0.5" data-id="element-752">
                              {loc.address}
                            </div>
                          </div>
                        </button>)}
                    </div>
                  </div>}

                <p className="text-xs font-semibold text-stone uppercase tracking-widest mb-3" data-id="element-753">
                  Selecciona una fecha
                </p>
                <Calendar selectedDate={selectedDate} onSelectDate={handleDateSelect} availableDates={availableDates} data-id="element-754" />

                {selectedDate && <div className="animate-fade-slide-up" data-id="element-755">
                    <p className="text-xs font-semibold text-stone uppercase tracking-widest mb-3" data-id="element-756">
                      Horarios disponibles
                    </p>

                    {loadingSessions ? <div className="flex items-center justify-center py-10" data-id="element-757">
                        <Loader2 size={24} className="animate-spin text-sage" data-id="element-758" />
                        <span className="ml-3 text-sm text-stone" data-id="element-759">
                          Cargando horarios...
                        </span>
                      </div> : <div className="flex flex-col gap-2 mb-6" data-id="element-760">
                        {displayTimeslots.map((slot, i) => <button key={slot.sessionId || i} disabled={slot.available === 0} onClick={() => setSelectedTimeslot(slot)} className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${slot.available === 0 ? 'opacity-50 cursor-not-allowed bg-sand border-transparent' : selectedTimeslot?.time === slot.time && selectedTimeslot?.sessionId === slot.sessionId ? 'border-sage bg-white ring-4 ring-sage/10' : 'border-transparent bg-sand hover:border-sage hover:bg-white'}`} data-id="element-761">
                            <span className="font-semibold text-[0.95rem]" data-id="element-762">
                              {slot.time}
                            </span>
                            <div className="flex items-center gap-3" data-id="element-763">
                              <span className={`text-xs ${slot.available === 0 ? 'text-error font-semibold' : slot.available <= 3 ? 'text-warning font-semibold' : 'text-stone'}`} data-id="element-764">
                                {slot.available === 0 ? 'Agotado' : slot.available <= 3 ? `¡Solo ${slot.available} lugares!` : `${slot.available}/${slot.spots} lugares`}
                              </span>
                              <span className={`px-2.5 py-1 rounded-full text-[0.7rem] font-bold flex items-center gap-1 ${activeTab === 'virtual' ? 'bg-virtual-light text-virtual' : 'bg-presencial-light text-sage'}`} data-id="element-765">
                                {activeTab === 'virtual' ? <>
                                    <Monitor size={12} data-id="element-766" /> Zoom
                                  </> : <>
                                    <MapPin size={12} data-id="element-767" /> Presencial
                                  </>}
                              </span>
                            </div>
                          </button>)}
                      </div>}
                  </div>}

                <Button className="w-full flex items-center justify-center gap-2" disabled={!selectedDate || !selectedTimeslot} onClick={() => handleNextStep(2)} data-id="element-768">
                  Continuar <ArrowRight size={16} data-id="element-769" />
                </Button>
              </div>}

            {/* STEP 2: Personal Info */}
            {currentStep === 2 && <div className="animate-fade-slide-up" data-id="element-770">
                <h3 className="font-display text-xl text-earth mb-5" data-id="element-771">
                  Tus datos
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-772">
                  <div data-id="element-773">
                    <label className="block text-sm font-semibold text-earth mb-2" data-id="element-774">
                      Nombre *
                    </label>
                    <input type="text" className={`w-full p-3 border-2 rounded-xl text-sm outline-none transition-all ${errors.firstName ? 'border-error focus:ring-4 focus:ring-error/10' : 'border-mist focus:border-sage focus:ring-4 focus:ring-sage/10'}`} placeholder="María" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} data-id="element-775" />
                    {errors.firstName && <span className="text-xs text-error mt-1.5 block" data-id="element-776">
                        {errors.firstName}
                      </span>}
                  </div>
                  <div data-id="element-777">
                    <label className="block text-sm font-semibold text-earth mb-2" data-id="element-778">
                      Apellido *
                    </label>
                    <input type="text" className={`w-full p-3 border-2 rounded-xl text-sm outline-none transition-all ${errors.lastName ? 'border-error focus:ring-4 focus:ring-error/10' : 'border-mist focus:border-sage focus:ring-4 focus:ring-sage/10'}`} placeholder="González" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} data-id="element-779" />
                    {errors.lastName && <span className="text-xs text-error mt-1.5 block" data-id="element-780">
                        {errors.lastName}
                      </span>}
                  </div>
                </div>

                <div className="mb-4" data-id="element-781">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-782">
                    Email *
                  </label>
                  <input type="email" className={`w-full p-3 border-2 rounded-xl text-sm outline-none transition-all ${errors.email ? 'border-error focus:ring-4 focus:ring-error/10' : 'border-mist focus:border-sage focus:ring-4 focus:ring-sage/10'}`} placeholder="maria@gmail.com" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} data-id="element-783" />
                  {errors.email && <span className="text-xs text-error mt-1.5 block" data-id="element-784">
                      {errors.email}
                    </span>}
                </div>

                <div className="mb-4" data-id="element-785">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-786">
                    WhatsApp (opcional)
                  </label>
                  <input type="tel" className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10" placeholder="+52 55 1234 5678" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} data-id="element-787" />
                </div>

                <div className="mb-6" data-id="element-788">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-789">
                    Tu nivel de yoga
                  </label>
                  <select className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10 appearance-none bg-white" value={formData.level} onChange={e => setFormData({
                ...formData,
                level: e.target.value
              })} data-id="element-790">
                    <option value="" data-id="element-791">Selecciona tu nivel</option>
                    <option value="principiante" data-id="element-792">
                      Principiante (primera vez)
                    </option>
                    <option value="basico" data-id="element-793">Básico (0–6 meses)</option>
                    <option value="intermedio" data-id="element-794">Intermedio (6+ meses)</option>
                    <option value="avanzado" data-id="element-795">Avanzado</option>
                  </select>
                </div>

                <div className="flex gap-3" data-id="element-796">
                  <Button variant="outline" className="flex-1 flex items-center justify-center gap-2" onClick={() => handleNextStep(1)} data-id="element-797">
                    <ArrowLeft size={16} data-id="element-798" /> Atrás
                  </Button>
                  <Button className="flex-[2] flex items-center justify-center gap-2" onClick={() => handleNextStep(3)} data-id="element-799">
                    Continuar <ArrowRight size={16} data-id="element-800" />
                  </Button>
                </div>
              </div>}

            {/* STEP 3: Summary & Payment */}
            {currentStep === 3 && <div className="animate-fade-slide-up" data-id="element-801">
                <div className="bg-sand rounded-2xl p-5 mb-6" data-id="element-802">
                  <p className="font-display text-base text-earth mb-4" data-id="element-803">
                    📋 Resumen de tu reserva
                  </p>
                  <div className="flex justify-between py-2.5 border-b border-mist text-sm" data-id="element-804">
                    <span className="text-stone" data-id="element-805">Tipo de clase</span>
                    <span className="font-medium text-earth" data-id="element-806">
                      {activeTab === 'presencial' ? 'Yoga en Estudio' : 'Yoga por Zoom'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2.5 border-b border-mist text-sm" data-id="element-807">
                    <span className="text-stone" data-id="element-808">Fecha y hora</span>
                    <span className="font-medium text-earth" data-id="element-809">
                      {selectedDate?.toLocaleDateString('es-MX')} -{' '}
                      {selectedTimeslot?.time}
                    </span>
                  </div>
                  <div className="flex justify-between py-2.5 border-b border-mist text-sm" data-id="element-810">
                    <span className="text-stone" data-id="element-811">Ubicación</span>
                    <span className="font-medium text-earth" data-id="element-812">
                      {activeTab === 'presencial' ? locations.find(l => l.id === selectedLocation)?.name : 'Online (Zoom)'}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 text-base font-bold" data-id="element-813">
                    <span className="text-earth" data-id="element-814">Total a pagar</span>
                    <span className="text-sage" data-id="element-815">{selectedPrice}</span>
                  </div>
                </div>

                <StripePaymentForm onPaymentSuccess={paymentMethodId => handleConfirm(paymentMethodId)} onBack={() => handleNextStep(2)} isProcessing={isConfirming} setIsProcessing={setIsConfirming} customerName={`${formData.firstName} ${formData.lastName}`} customerEmail={formData.email} selectedPrice={selectedPrice} data-id="element-816" />
              </div>}

            {/* STEP 4: Success */}
            {currentStep === 4 && <div className="text-center py-8 animate-fade-slide-up" data-id="element-817">
                <div className="w-20 h-20 bg-gradient-to-br from-sage to-[#5a8a5e] rounded-full flex items-center justify-center text-4xl mx-auto mb-6 animate-scale-in" data-id="element-818">
                  <CheckCircle className="w-10 h-10 text-white" data-id="element-819" />
                </div>
                <h3 className="font-display text-3xl text-earth mb-3" data-id="element-820">
                  ¡Reserva confirmada!
                </h3>
                <p className="text-stone text-sm leading-relaxed mb-6" data-id="element-821">
                  Enviamos los detalles a <strong data-id="element-822">{formData.email}</strong>.
                  Recibirás un recordatorio 24h y 2h antes de tu clase.
                </p>
                <div className="bg-sand rounded-2xl p-5 text-left mb-6" data-id="element-823">
                  <p className="text-sm text-earth mb-1" data-id="element-824">
                    <strong data-id="element-825">Clase:</strong>{' '}
                    {activeTab === 'presencial' ? 'Yoga Presencial' : 'Yoga Virtual'}
                  </p>
                  <p className="text-sm text-earth mb-1" data-id="element-826">
                    <strong data-id="element-827">Fecha:</strong>{' '}
                    {selectedDate?.toLocaleDateString('es-MX')} a las{' '}
                    {selectedTimeslot?.time}
                  </p>
                  <p className="text-sm text-earth" data-id="element-828">
                    <strong data-id="element-829">Lugar:</strong>{' '}
                    {activeTab === 'presencial' ? locations.find(l => l.id === selectedLocation)?.name : 'Enlace de Zoom en tu email'}
                  </p>
                </div>
                <Button variant="sage" className="w-full" onClick={resetBooking} data-id="element-830">
                  📅 Agendar otra clase
                </Button>
              </div>}
          </div>
        </Reveal>
      </div>
    </section>;
}